import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { getDomain } from '../../utils/functions';
import Page from '../../components/Page';
import Spinner from '../../components/Spinner';
import IconMenu from '../../components/IconMenu';
import { Customer, CustomerData } from '../../types/customer';
import { routes } from '../../utils/routes';
import Button from '../../components/Button';
import LoyaltyCard from './LoyaltyCard/LoyaltyCard';

interface Props {
  customer?: Customer;
  cdpCustomer?: CustomerData;
  idToken: string;
}

interface CustomerField {
  fieldName: string;
  fieldData: string | null;
}

const ProfilePage: React.FC<Props> = (props: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [loyaltyModalIsOpen, setLoyaltyModalIsOpen] = useState(false);
  const [customerFields, setCustomerFields] = useState<CustomerField[]>();
  const [barcode, setBarcode] = useState<string>();
  const { t, i18n } = useTranslation(['profile_page']);
  const domain = getDomain();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_PROFILE_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_PROFILE_PAGE_PYHA ?? '';

  const { inProgress } = useMsal();

  // Check if user should be redirected after logging in.
  useEffect(() => {
    if (props.customer && inProgress === InteractionStatus.None) {
      const postLoginRedirect =
        window.sessionStorage.getItem('postLoginRedirect');

      if (postLoginRedirect === 'iticket') {
        window.sessionStorage.removeItem('postLoginRedirect');
        window.location.href = `/${i18n.language}/${
          routes.ITICKET_REDIRECT[i18n.language]
        }`;
        return;
      }

      const currentUrl = new URL(window.location.href);
      const redirectUri = currentUrl.searchParams.get('redirect_uri');
      const callbackUrl = currentUrl.searchParams.get('callback_url') ?? '';

      if (redirectUri === 'iticket') {
        window.sessionStorage.setItem('callback_url', callbackUrl);

        window.location.href = `/${i18n.language}/${
          routes.ITICKET_REDIRECT[i18n.language]
        }`;
        return;
      }
    }
  }, [inProgress, props.customer, i18n.language]);

  useEffect(() => {
    const domains: { [key: string]: string } = {
      ruka: '.ruka.fi',
      pyha: '.pyha.fi',
    };
    Cookies.set('logged_in', 'true', {
      path: '/',
      domain: domains[getDomain()],
      expires: 1,
    });
  }, []);

  useEffect(() => {
    if (props.cdpCustomer?.cdpData && props.cdpCustomer.cdpData) {
      const barcodeRef = props.cdpCustomer.cdpData.references.find(
        (reference: { provider: string; property: string }) =>
          reference.provider === 'Tekso' && reference.property === 'EAN-13',
      );
      if (barcodeRef) {
        setBarcode(barcodeRef.value);
      }
    }
  }, [props.cdpCustomer]);

  useEffect(() => {
    if (props.customer) {
      const customer = [
        { fieldName: t('First name'), fieldData: props.customer.givenName },
        { fieldName: t('Last name'), fieldData: props.customer.surname },
        {
          fieldName: t('Email'),
          fieldData: props.customer.email,
        },
        {
          fieldName: t('Phone number'),
          fieldData: props.customer.mobilePhone,
        },
        {
          fieldName: t('Street address'),
          fieldData: props.customer.streetAddress,
        },
        {
          fieldName: t('Postal code'),
          fieldData: props.customer.postalCode,
        },
        {
          fieldName: t('City'),
          fieldData: props.customer.city,
        },
      ];
      setCustomerFields(customer);
      setIsLoading(false);
    }
  }, [props.customer, i18n.language]);

  let mainContent = <Spinner />;

  if (!isLoading && customerFields) {
    mainContent = (
      <>
        <ul className='bg-gray-400 p-5 md:flex md:flex-wrap'>
          {customerFields.map((customer, i) => (
            <li key={i} className='my-3 md:w-6/12 md:my-2'>
              <p className='font-bold'>{customer.fieldName}</p>
              <p className='my-1'>{customer.fieldData}</p>
            </li>
          ))}
        </ul>

        {props.customer && barcode && (
          <LoyaltyCard
            customer={props.customer}
            barcode={barcode}
            loyaltyModalIsOpen={loyaltyModalIsOpen}
            setLoyaltyModalIsOpen={setLoyaltyModalIsOpen}
          />
        )}

        {/* The update info button will not be available for the time being */}
        {/* <Button
          href='add the correct url here'
          external={true}
          className='my-5 mx-auto md:mx-0 w-full md:w-fit'
        >
          {t('Update information') ?? ''}
        </Button> */}
      </>
    );
  }

  return (
    <>
      <Page
        pageId={pageId[domain]}
        mainContent={mainContent}
        topContent={<IconMenu customer={props.customer} />}
        heroButton={
          process.env.REACT_APP_LOYALTY_CARD_ACTIVE &&
          process.env.REACT_APP_LOYALTY_CARD_ACTIVE === 'true' && // Note: this check should be removed eventually.
          props.customer &&
          barcode ? (
            <Button onClick={() => setLoyaltyModalIsOpen(true)}>
              {t('Open the loyalty card')}
            </Button>
          ) : null
        }
      />
    </>
  );
};

export default ProfilePage;
